/**
 * What a given colour order would do, worked out with the engine's own
 * `distribute` and `trajanTileSatisfied` rather than a second implementation of
 * the action circle. Pure, so the consequences of a swap can be tested without
 * a DOM — and so the mat never has to reason about the rules itself.
 */
import {
  asTray,
  distribute,
  trajanTileSatisfied,
  type Color,
  type PlayerView,
  type TrayIndex,
} from '@trajan/engine';

/** One marker on its way: where it sits in the order, and where it lands. */
export interface Drop {
  /** Index in the colour order — the identity a swap moves around. */
  index: number;
  color: Color;
  tray: TrayIndex;
  /** True for the marker that decides the action: the last one dropped. */
  last: boolean;
}

export interface Plan {
  source: TrayIndex;
  /** The tray the last marker lands in; its action is the one performed. */
  target: TrayIndex;
  /** Markers moved, which is also the time this costs. */
  moved: number;
  order: Color[];
  drops: Drop[];
  /** Each tray as it would stand afterwards. */
  after: Color[][];
  /**
   * Slot indices whose Trajan tile the move would satisfy. Only the one beside
   * the target can be accomplished this turn; the rest are what it sets up.
   */
  satisfied: number[];
  /** True when the tile beside the target would be completed — the payoff now. */
  completesTarget: boolean;
}

/** What the markers of `source` would do, arranged in `order`. Null if that tray is empty. */
export function planPlacement(
  player: Pick<PlayerView, 'trays' | 'trajanSlots'>,
  source: TrayIndex,
  order?: readonly Color[],
  slotBesideTray: readonly TrayIndex[] = [0, 1, 2, 3, 4, 5] as TrayIndex[],
): Plan | null {
  const d = distribute(player.trays, source, order);
  if (!d) return null;

  const drops: Drop[] = d.colorOrder.map((color, index) => ({
    index,
    color,
    tray: asTray(source + index + 1),
    last: index === d.colorOrder.length - 1,
  }));

  const satisfied: number[] = [];
  player.trajanSlots.forEach((tile, slot) => {
    if (!tile) return;
    const tray = slotBesideTray.indexOf(slot as TrayIndex);
    const contents = tray >= 0 ? d.trays[tray] : undefined;
    if (contents && trajanTileSatisfied(tile, contents)) satisfied.push(slot);
  });

  const targetSlot = slotBesideTray[d.target];
  return {
    source,
    target: d.target,
    moved: d.moved,
    order: d.colorOrder,
    drops,
    after: d.trays,
    satisfied,
    completesTarget: targetSlot !== undefined && satisfied.includes(targetSlot),
  };
}

/** The order with positions `a` and `b` exchanged. */
export function swap(order: readonly Color[], a: number, b: number): Color[] {
  const out = [...order];
  const x = out[a];
  const y = out[b];
  if (x === undefined || y === undefined) return out;
  out[a] = y;
  out[b] = x;
  return out;
}

/** The order with the marker at `from` lifted out and put back at `to`. */
export function move(order: readonly Color[], from: number, to: number): Color[] {
  if (from === to || from < 0 || to < 0 || from >= order.length || to >= order.length) {
    return [...order];
  }
  const out = [...order];
  const [lifted] = out.splice(from, 1);
  if (lifted === undefined) return [...order];
  out.splice(to, 0, lifted);
  return out;
}

// ---------------------------------------------------------------------------
// Setup: arranging all twelve markers, before the game begins
// ---------------------------------------------------------------------------

/**
 * A marker's place on the mat while arranging: which tray, and where in it.
 * Flattened to one number so the same drag, tap and keyboard handling that
 * moves a marker within a tray can move one between trays.
 */
export const markerAt = (tray: number, slot: number, perTray: number): number =>
  tray * perTray + slot;

/**
 * Swap two markers, wherever they sit.
 *
 * Swapping is the only move offered, and that is a rule rather than a
 * simplification: the engine wants six trays of exactly two markers, using
 * exactly two of each colour, and an exchange cannot break either count. So
 * every arrangement the mat can reach is one `apply` accepts — and since any
 * permutation is a sequence of exchanges, every arrangement it would accept
 * can be reached.
 */
export function swapMarkers(
  layout: readonly (readonly Color[])[],
  a: number,
  b: number,
  perTray: number,
): Color[][] {
  const next = layout.map((t) => [...t]);
  const at = (i: number) => [Math.floor(i / perTray), i % perTray] as const;
  const [ta, sa] = at(a);
  const [tb, sb] = at(b);
  const rowA = next[ta];
  const rowB = next[tb];
  if (!rowA || !rowB || rowA[sa] === undefined || rowB[sb] === undefined) return next;
  const keep = rowA[sa];
  rowA[sa] = rowB[sb];
  rowB[sb] = keep;
  return next;
}
